import { cn } from '@/lib/utils.ts'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Separator } from '@/components/ui/separator.tsx'

import { ManagedLabel } from '@/components/form/ManagedLabel.tsx'
import { useMutateLabel } from '@/requests/mutations/useMutateLabel.ts'
import { getTemporaryId } from '@/utils/key.ts'
import { GlobalPubSub } from '@/utils/pubsub.ts'
import '@/components/scrollbar.css'
import { useSubscription } from '@/hooks/useSubscription.ts'
import { useQueryLabels } from '@/requests/queries/useQueryProject.ts'
import { onEnterInclusive } from '@/utils/keyboard.ts'

export const LabelManagementModal = () => {
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [newLabelId, setNewLabelId] = useState(getTemporaryId())
  const [filteredLabels, setFilteredLabels] = useState<Label[]>([])
  const { labels } = useQueryLabels()

  const { create } = useMutateLabel()

  useSubscription([[GlobalPubSub.Signals.TOGGLE_LABEL_MANAGEMENT_MODAL, setOpen as Callback]])

  useEffect(() => {
    setFilteredLabels(labels.filter((label) => label.name.includes(searchText)))
  }, [labels, searchText])

  const addLabel = () => {
    create({ id: newLabelId, name: searchText, count: 0, __typename: 'Label' }).then(() => {
      setSearchText('')
      setNewLabelId(getTemporaryId())
    })
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="[&>button:last-child]:hidden max-h-screen flex flex-col gap-0 rounded-lg py-4 px-0 bg-surface-backgroundElevated">
          <DialogHeader className="px-4">
            <DialogTitle className="font-EN-KR-sub-heading-bold text-contents-primary">
              Labels ({labels.length})
            </DialogTitle>
            <DialogDescription className="sr-only" />
          </DialogHeader>

          <Separator className="my-4" />

          <div className="px-4 flex flex-col gap-2">
            <Input
              type="text"
              value={searchText}
              onKeyDown={(e) => onEnterInclusive(e, () => filteredLabels.length === 0 && addLabel())}
              onChange={({ target: { value } }) => setSearchText(value)}
              placeholder="Search or add a new label"
            />
            <div className="max-h-[660px] relative overflow-y-auto tracker-scrollbar">
              {filteredLabels.map((label) => (
                <ManagedLabel key={label.id} modes={['view', 'edit']} label={label} />
              ))}
            </div>
            {filteredLabels.length === 0 && (
              <div className="w-full h-40 flex flex-col justify-center items-center font-EN-KR-body1-regular text-contents-primary">
                <div>
                  No results found for '<span className="font-EN-KR-body1-bold text-labelType-label">{searchText}</span>
                  '
                </div>
                <div>Press enter or click te add button to add</div>
              </div>
            )}
          </div>
          <DialogFooter className="px-4 mt-4">
            <div
              className={cn(
                'flex items-center w-full',
                filteredLabels.length === 0 ? 'justify-between' : 'justify-end',
              )}
            >
              <DialogClose asChild>
                <Button type="button" variant="outline" className="h-9 font-EN-KR-body1-regular">
                  Close
                </Button>
              </DialogClose>
              {filteredLabels.length === 0 && (
                <Button type="button" variant="primary" onClick={addLabel} className="h-9 font-EN-KR-body1-bold">
                  Add
                </Button>
              )}
            </div>
            <DialogClose asChild>
              <Button type="button" size="icon" variant="ghost" className="absolute top-[18px] right-[16px]">
                <img src="/images/close-dialog-icon.svg" alt="close" />
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
